/**
 * Payment Service API Contracts
 * Request/Response schemas for the payment service
 */

import {
  CHECKOUT_METHODS,
  CustomerIdSchema,
  PAYMENT_FEE_TYPES,
  PAYMENT_METHOD_CODES,
  REFUND_STATUSES,
} from '@autopia/shared-types';
import { z } from 'zod';

import {
  CheckoutGroupSchema,
  ConcreteSalesChannelSchema,
  PaymentAmountBoundSchema,
} from './configuration';

// ============================================================================
// Payment Methods
// ============================================================================

/**
 * Coarse, customer-facing methods chosen at CHECKOUT (input). At checkout the
 * specific bank/channel is not yet known, so this stays generic. Do not use
 * this for filtering or for the stored value — see {@link PaymentMethodCodeSchema}.
 *
 * Derived from {@link CHECKOUT_METHODS} in @autopia/shared-types, which is the
 * single declaration. Never re-list the values here: this schema and
 * payment-service's routing must not be able to disagree.
 */
export const CheckoutMethodSchema = z.enum(CHECKOUT_METHODS);

export type CheckoutMethod = z.infer<typeof CheckoutMethodSchema>;

/**
 * Canonical, gateway-resolved codes that are STORED in `Payment.paymentMethod`
 * and used to FILTER the admin payments list (e.g. BCA_VA, GOPAY, QRIS).
 * Single source of truth: {@link PAYMENT_METHOD_CODES} in @autopia/shared-types.
 */
export const PaymentMethodCodeSchema = z.enum(PAYMENT_METHOD_CODES);

export type PaymentMethodCode = z.infer<typeof PaymentMethodCodeSchema>;

export const PaymentGatewaySchema = z.enum(['midtrans', 'xendit', 'doku', 'manual']);

export type PaymentGateway = z.infer<typeof PaymentGatewaySchema>;

/**
 * Derived from {@link PAYMENT_FEE_TYPES} in @autopia/shared-types. This enum was
 * inlined twice in this file — on `AvailablePaymentMethodSchema` and on
 * `FeeCalculationSchema` — which is two declarations of one vocabulary, free to
 * drift the moment a fourth fee type is needed.
 */
export const PaymentFeeTypeSchema = z.enum(PAYMENT_FEE_TYPES);

export type PaymentFeeType = z.infer<typeof PaymentFeeTypeSchema>;

/**
 * The lowercase vocabulary crossing the HTTP boundary.
 *
 * Mirrors the payment-service Prisma `PaymentStatus` enum and the uppercase
 * `PaymentStatusSchema` in @autopia/event-schemas value for value — nine
 * values, differing only in case. Convert explicitly at the boundary; 'paid'
 * vs 'PAID' does not fail loudly if the two are confused.
 *
 * `challenge` was missing here until #115 while the database, the Kafka
 * vocabulary and even the admin app's `paymentChallenge` translation all had
 * it. A payment held for Midtrans fraud review is a real state the database can
 * hold, so a response schema that rejects it rejects a live row.
 */
export const PaymentStatusSchema = z.enum([
  'pending',
  'processing',
  'paid',
  'failed',
  'expired',
  'cancelled',
  'refunded',
  'partially_refunded',
  'challenge',
]);

export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

// ============================================================================
// Payment Creation
// ============================================================================

export const CreatePaymentRequestSchema = z.object({
  orderId: z.string().uuid(),
  amount: z.number().int().positive(),
  paymentMethod: CheckoutMethodSchema,
  customerEmail: z.string().email(),
  customerName: z.string().max(100),
  customerPhone: z.string().max(20).optional(),
  returnUrl: z.string().url().optional(),
  callbackUrl: z.string().url().optional(),
  metadata: z.record(z.string()).optional(),
});

export type CreatePaymentRequest = z.infer<typeof CreatePaymentRequestSchema>;

export const PaymentResponseSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  amount: z.number().int(),
  currency: z.literal('IDR'),
  paymentMethod: CheckoutMethodSchema,
  status: PaymentStatusSchema,
  gatewayProvider: PaymentGatewaySchema.nullable(),
  gatewayReference: z.string().nullable(),
  customerEmail: z.string(),
  customerName: z.string(),
  expiresAt: z.string().datetime(),
  paidAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Payment action details
  paymentAction: z
    .object({
      type: z.enum(['redirect', 'qr_code', 'deeplink', 'none']),
      redirectUrl: z.string().url().optional(),
      qrCodeUrl: z.string().url().optional(),
      qrCodeString: z.string().optional(),
      deeplinkUrl: z.string().optional(),
      instructions: z.array(z.string()).optional(),
    })
    .optional(),
});

export type PaymentResponse = z.infer<typeof PaymentResponseSchema>;

// ============================================================================
// Payment Status
// ============================================================================

export const GetPaymentStatusResponseSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  status: PaymentStatusSchema,
  amount: z.number().int(),
  paidAmount: z.number().int().nullable(),
  paymentMethod: CheckoutMethodSchema,
  gatewayProvider: PaymentGatewaySchema.nullable(),
  gatewayReference: z.string().nullable(),
  gatewayStatus: z.string().nullable(),
  paidAt: z.string().datetime().nullable(),
  failedAt: z.string().datetime().nullable(),
  failureReason: z.string().nullable(),
  expiresAt: z.string().datetime(),
});

export type GetPaymentStatusResponse = z.infer<typeof GetPaymentStatusResponseSchema>;

// ============================================================================
// Payment Methods (Available for checkout)
// ============================================================================

/**
 * One method offered to a customer by `GET /v1/payment-methods` (#793).
 *
 * ## Four deliberate changes made by #781, and why each was safe
 *
 * This schema predates the payment configuration epic and described something
 * the service was never going to serve. Nothing produced or consumed it — no
 * controller in `services/payment-service/src` serves the path and no app reads
 * the type — so all four changes moved no wire value. `payment-configuration.md`
 * §6.1 lists them; they are recorded here because the next reader will otherwise
 * see fields go missing and assume a regression.
 *
 * 1. **`name` and `description` are gone.** They were required, and the endpoint
 *    serves no labels: each app resolves them from the canonical enum through its
 *    own i18n store (§8). A label on the wire would have been a fourth place the
 *    same string lives, and the only one an operator could change without a
 *    deploy — so the three that need a deploy would drift behind it.
 * 2. **`gateway` is gone.** It was `PaymentGatewaySchema`, a closed four-value
 *    enum that cannot express `edc-permata`; more fundamentally it names ONE
 *    provider where routing is now an ordered list with a failover chain. There
 *    is no honest single value to put here, and a customer-facing response has no
 *    use for the chain — which provider takes the money is the server's decision
 *    and can change between rendering the picker and charging.
 * 3. **`minAmount` / `maxAmount` are nullable** and no longer required, matching
 *    the `BigInt?` columns behind them. `maxAmount` was `z.number().int().positive()`,
 *    which cannot represent "no upper bound" at all — the value most methods have.
 * 4. **`feeType` is uppercase**, because `PAYMENT_FEE_TYPES` is (see its note in
 *    `@autopia/shared-types`). The database, this contract and the Kafka payload
 *    now agree member for member, so no converter sits at any boundary.
 *
 * `code` was already keyed on the coarse checkout vocabulary and stays there —
 * that is the level the catalogue uses and the level a customer picks at (§4.6).
 */
export const AvailablePaymentMethodSchema = z.object({
  code: CheckoutMethodSchema,
  /** The picker header this method sits under. Resolved to words client-side. */
  checkoutGroup: CheckoutGroupSchema,
  iconUrl: z.string().url().optional(),
  /** `null` means no lower bound. */
  minAmount: PaymentAmountBoundSchema.nullable(),
  /** `null` means no upper bound — which is what most methods have. */
  maxAmount: PaymentAmountBoundSchema.nullable(),
  feeType: PaymentFeeTypeSchema,
  feeValue: z.number().nonnegative(),
  estimatedSettlement: z.string().optional(),
  isEnabled: z.boolean(),
});

export type AvailablePaymentMethod = z.infer<typeof AvailablePaymentMethodSchema>;

/**
 * Query of `GET /v1/payment-methods` (#793).
 *
 * `salesChannel` excludes the `ALL` sentinel: a real checkout happens on one
 * channel, and `ALL` is the scope of a configuration row. Accepting it would
 * return the union of every channel's methods and look like a working filter.
 */
export const GetPaymentMethodsQuerySchema = z.object({
  salesChannel: ConcreteSalesChannelSchema,
  /** Filters out methods whose bounds exclude this amount. IDR. */
  amount: z.coerce.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
});

export type GetPaymentMethodsQuery = z.infer<typeof GetPaymentMethodsQuerySchema>;

export const GetPaymentMethodsResponseSchema = z.object({
  methods: z.array(AvailablePaymentMethodSchema),
});

export type GetPaymentMethodsResponse = z.infer<typeof GetPaymentMethodsResponseSchema>;

// ============================================================================
// Refunds
// ============================================================================

/**
 * Derived from {@link REFUND_STATUSES} in @autopia/shared-types — the single
 * declaration. Never re-list the values here.
 *
 * Distinct from {@link AdminRefundStatusSchema}, which is the admin console's
 * own uppercase workflow vocabulary and a genuinely different set of states.
 */
export const RefundStatusSchema = z.enum(REFUND_STATUSES);

export type RefundStatus = z.infer<typeof RefundStatusSchema>;

/**
 * Customer-facing: request a refund for an order.
 * orderId comes from the route param — only body fields are here.
 */
export const CreateRefundRequestSchema = z.object({
  reason: z.string().min(1).max(500),
  refundAmount: z.number().int().positive().optional(),
  notes: z.string().max(1000).optional(),
});

export type CreateRefundRequest = z.infer<typeof CreateRefundRequestSchema>;

/** Admin: paginated list of refunds with optional status filter. */
export const ListRefundsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: RefundStatusSchema.optional(),
});

export type ListRefundsQuery = z.infer<typeof ListRefundsQuerySchema>;

/** Admin: approve and process a refund. */
export const ProcessRefundSchema = z.object({
  notes: z.string().max(1000).optional(),
});

export type ProcessRefund = z.infer<typeof ProcessRefundSchema>;

/** Admin: reject a refund request. */
export const RejectRefundSchema = z.object({
  rejectedReason: z.string().min(1).max(500),
});

export type RejectRefund = z.infer<typeof RejectRefundSchema>;

export const RefundResponseSchema = z.object({
  id: z.string(),
  paymentId: z.string(),
  orderId: z.string(),
  amount: z.number().int(),
  status: RefundStatusSchema,
  reason: z.string().nullable(),
  gatewayRefundId: z.string().nullable(),
  processedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export type RefundResponse = z.infer<typeof RefundResponseSchema>;

// ============================================================================
// Payment Queries
// ============================================================================

export const GetPaymentsQuerySchema = z.object({
  orderId: z.string().uuid().optional(),
  /**
   * Filter to one customer's payments (#857) — an identity-service `User.id`.
   *
   * `CustomerIdSchema`, so a sentinel or an email is a 400 rather than a query
   * that quietly matches nothing. Derived, not restated (Rule #7c).
   *
   * ⚠️ **This narrows; it never widens.** `listPayments` already scopes a
   * non-privileged caller to their own payments, and this filter composes with
   * that scope rather than replacing it — a customer asking for someone else's
   * id gets an empty page, not that person's ledger. The composition is
   * asserted in `payments.service.spec.ts`, because "add a filter to a list
   * endpoint that is also the authorization boundary" is exactly the shape of
   * the defect #594 reported.
   */
  customerId: CustomerIdSchema.optional(),
  status: PaymentStatusSchema.optional(),
  // Filter by the canonical stored code (BCA_VA, GOPAY, …), not the coarse
  // checkout method — the admin filter dropdown sends these codes directly.
  paymentMethod: PaymentMethodCodeSchema.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetPaymentsQuery = z.infer<typeof GetPaymentsQuerySchema>;

/**
 * One row of `GET /api/v1/payments` (#857).
 *
 * ⚠️ **This is not {@link PaymentResponseSchema}.** The list is a deliberately
 * minimal projection — no `customerEmail`, no `customerName`, no
 * `paymentAction` — and the difference is not laziness: those are the
 * gateway-facing PII fields ADR-037 decision 2 keeps off identifiers, and a
 * list endpoint hands back many rows at once. `amount` is a **string** here
 * because the service returns `Decimal.toString()`, and `status` is the
 * **uppercase** Prisma vocabulary rather than the lowercase HTTP one, matching
 * what `listPayments` actually maps. Written against the implementation, not
 * against what the response "should" look like — `apps/web` already keeps a
 * hand-written `PaymentSummarySchema` for exactly this reason, because
 * `PaymentResponseSchema` requires fields these endpoints omit.
 */
export const PaymentListItemSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  /**
   * The payment's owner — an identity-service `User.id`.
   *
   * **Nullable, and the null is meaningful.** ADR-037 decision 3 makes `NULL`
   * the correct owner for a genuine guest, and decision 1a exempts
   * `SERVICE_CHARGE`, which has no buyer at all. So an absent owner is a fact
   * about the payment, not a gap — `null` rather than omitted, so a consumer
   * cannot mistake "no owner" for "field not returned".
   *
   * Before #857 no payment contract carried an id at all: identity was exposed
   * only as free-text email and name, which decision 2 excludes from being an
   * identifier at all.
   */
  customerId: z.string().nullable(),
  status: z.string(),
  amount: z.string(),
  currency: z.string(),
  paymentMethod: z.string(),
  paidAt: z.string().nullable(),
  expiresAt: z.string(),
  createdAt: z.string(),
});

export type PaymentListItem = z.infer<typeof PaymentListItemSchema>;

/** Envelope of `GET /api/v1/payments` — `{ data, pagination }`, not `{ meta }`. */
export const PaymentsListResponseSchema = z.object({
  data: z.array(PaymentListItemSchema),
  pagination: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
  }),
});

export type PaymentsListResponse = z.infer<typeof PaymentsListResponseSchema>;

// ============================================================================
// Webhook Validation
// ============================================================================

export const MidtransWebhookSchema = z.object({
  transaction_time: z.string(),
  transaction_status: z.string(),
  transaction_id: z.string(),
  status_message: z.string(),
  status_code: z.string(),
  signature_key: z.string(),
  payment_type: z.string(),
  order_id: z.string(),
  merchant_id: z.string(),
  gross_amount: z.string(),
  fraud_status: z.string().optional(),
  currency: z.string(),
});

export type MidtransWebhook = z.infer<typeof MidtransWebhookSchema>;

// ============================================================================
// Payment Summary (Admin)
// ============================================================================

export const PaymentSummarySchema = z.object({
  date: z.string(),
  totalTransactions: z.number().int(),
  successfulTransactions: z.number().int(),
  failedTransactions: z.number().int(),
  totalAmountIdr: z.number().int(),
  successAmountIdr: z.number().int(),
  refundedAmountIdr: z.number().int(),
  byPaymentMethod: z.array(
    z.object({
      // The STORED code, not the checkout selection: payments.service.ts's
      // getPaymentStats builds this with groupBy(['paymentMethod']), and that
      // column holds the granular value the resolver wrote (BCA_VA, GOPAY).
      // This was annotated with the coarse checkout schema, which contains
      // none of those — the contract described values the query cannot return.
      method: PaymentMethodCodeSchema,
      count: z.number().int(),
      amountIdr: z.number().int(),
    })
  ),
});

export type PaymentSummary = z.infer<typeof PaymentSummarySchema>;

// ============================================================================
// Midtrans Snap Integration (PAY-004)
// ============================================================================

/**
 * Request schema for initiating a payment session.
 * successRedirectUrl and failureRedirectUrl are supplied by the frontend so
 * each client controls its own post-payment destinations without requiring a
 * WEB_URL environment variable change in the payment-service.
 */
export const InitiatePaymentRequestSchema = z.object({
  orderId: z.string().uuid(),
  paymentMethod: CheckoutMethodSchema,
  successRedirectUrl: z.string().url(),
  failureRedirectUrl: z.string().url(),
});

export type InitiatePaymentRequest = z.infer<typeof InitiatePaymentRequestSchema>;

/**
 * Response schema for a Midtrans Snap payment initiation.
 * qrCodeUrl/paymentPlatform confirmed against payments.service.ts's actual
 * return statements (both idempotent-replay and fresh-initiate branches) and
 * the backend's own initiate-payment.dto.ts, which declares both optional.
 */
export const InitiatePaymentResponseSchema = z.object({
  paymentId: z.string(),
  orderId: z.string(),
  snapToken: z.string().optional(),
  redirectUrl: z.string(),
  clientKey: z.string(),
  expiresAt: z.string().datetime(),
  status: z.string(),
  qrCodeUrl: z.string().optional(),
  paymentPlatform: z.string().optional(),
});

export type InitiatePaymentResponse = z.infer<typeof InitiatePaymentResponseSchema>;

// ============================================================================
// Fee Calculation
// ============================================================================

export const CalculateFeesParamsSchema = z.object({
  amount: z.number().int().positive(),
  paymentMethod: z.string().min(1),
});

export type CalculateFeesParams = z.infer<typeof CalculateFeesParamsSchema>;

export const FeeCalculationSchema = z.object({
  amount: z.number().int(),
  fee: z.number().int(),
  total: z.number().int(),
  feeType: PaymentFeeTypeSchema,
  feeValue: z.number(),
});

export type FeeCalculation = z.infer<typeof FeeCalculationSchema>;

/** Frontend-mapped shape returned after initiating a payment intent. */
export const PaymentIntentSchema = z.object({
  intentId: z.string(),
  clientSecret: z.string().optional(),
  redirectUrl: z.string().optional(),
  qrCodeUrl: z.string().optional(),
  deeplinkUrl: z.string().optional(),
  expiresAt: z.string(),
  paymentPlatform: z.string().optional(),
});

export type PaymentIntent = z.infer<typeof PaymentIntentSchema>;

// ============================================================================
// Service Charge (internal service-to-service payment, e.g. reseller deposits)
// ============================================================================

export const ServiceChargeRequestSchema = z.object({
  referenceId: z.string().min(1),
  referenceType: z.string().min(1),
  amount: z.number().int().positive(),
  customerName: z.string().min(1),
  customerEmail: z.string().email(),
  customerPhone: z.string().optional(),
  itemName: z.string().optional(),
  expiryMinutes: z.number().int().min(1).optional(),
  successRedirectUrl: z.string().optional(),
  failureRedirectUrl: z.string().optional(),
});

export type ServiceChargeRequest = z.infer<typeof ServiceChargeRequestSchema>;

export const ServiceChargeResponseSchema = z.object({
  paymentId: z.string(),
  gatewayProvider: z.string(),
  snapToken: z.string().optional(),
  clientKey: z.string().optional(),
  redirectUrl: z.string().optional(),
  qrCodeUrl: z.string().optional(),
  expiresAt: z.string(),
});

export * from './admin';
export * from './configuration';

export type ServiceChargeResponse = z.infer<typeof ServiceChargeResponseSchema>;

// ============================================================================
// Reconciliation — single-transaction "sync from gateway"
//
// Shared contract for POST /api/v1/reconciliation/sync/:orderId. The request
// has no body (orderId is the route param); only the response is modelled here.
// Amounts are serialized to strings on the wire because the payment-service
// stores them as BigInt — the controller String()-ifies internalAmount /
// gatewayAmount before returning. Consumed by the payment-service controller
// (return type / validation) and the admin app (query hook + sync components),
// replacing the previously hand-mirrored local types (governance rule #1).
// ============================================================================

export const ReconciliationDiscrepancyTypeSchema = z.enum([
  'status_mismatch',
  'amount_mismatch',
  'missing_internal',
  'missing_gateway',
]);

export type ReconciliationDiscrepancyType = z.infer<
  typeof ReconciliationDiscrepancyTypeSchema
>;

export const ReconciliationTransactionStatusSchema = z.object({
  orderId: z.string(),
  internalStatus: z.string(),
  gatewayStatus: z.string(),
  internalAmount: z.string(),
  gatewayAmount: z.string(),
  isMatched: z.boolean(),
  /**
   * True only when this call actually wrote the gateway's status back to the
   * internal record. A mismatch that was detected but NOT written leaves this
   * false — consumers must not read `isMatched: false` as "we fixed it".
   * Defaults to false so a response from a service that predates this field
   * is treated as "no write happened", which is the safe reading.
   */
  synced: z.boolean().default(false),
  discrepancyType: ReconciliationDiscrepancyTypeSchema.optional(),
  discrepancyDetails: z.string().optional(),
});

export type ReconciliationTransactionStatus = z.infer<
  typeof ReconciliationTransactionStatusSchema
>;

/** Response of POST /api/v1/reconciliation/sync/:orderId */
export const SyncTransactionResultSchema = z.object({
  success: z.boolean(),
  status: ReconciliationTransactionStatusSchema,
});

export type SyncTransactionResult = z.infer<typeof SyncTransactionResultSchema>;

// ============================================================================
// Admin payment detail — GET /api/v1/admin/payments/:id
//
// Backs the admin payment detail page's four rich sections. Declared here
// rather than in ./admin because index.ts re-exports that module (see the
// `export * from './admin'` above), so importing the enums below back from
// here would make the module graph circular and order-dependent.
//
// Deliberately NOT hosted on GET /v1/payments/:id: that route has five
// customer-facing callers, no ownership check, and a 1h terminal-state Redis
// cache that would serve refund history stale. See
// docs/design/features/admin-payment-detail.md §4.
// ============================================================================

/**
 * The gateway's own account of the transaction, as opposed to ours.
 *
 * Every field is read defensively out of `Payment.gatewayResponse`, which is a
 * union of six shapes written by six code paths where the last writer wins
 * (design doc §3.2). A field is null when the arm carrying it is not the arm
 * that was written — notably, a payment recovered by reconciliation keeps only
 * three keys, so most of this is null for exactly the payments an operator is
 * most likely to be looking at.
 *
 * PCI-DSS: last 4 card digits only. Never a full PAN, never CVV.
 */
export const AdminPaymentGatewayInfoSchema = z.object({
  /**
   * Lowercase, from {@link PaymentGatewaySchema} — the same HTTP vocabulary
   * #748 used for `Order.gateway`. The admin app previously kept its own
   * uppercase `GatewayName` copy; it was deleted rather than reconciled.
   */
  gatewayName: PaymentGatewaySchema,
  /** `Payment.gatewayReference`. */
  gatewayTransactionId: z.string().nullable(),
  /** The gateway's own status vocabulary (Midtrans `transaction_status`), not ours. */
  gatewayStatus: z.string().nullable(),
  /**
   * Which gateway *integration* handled this payment — `snap`, `qr_code`,
   * `virtual_account`, `redirect`.
   *
   * Distinct from `paymentMethod`, which says how the **customer** paid. Read
   * from the camelCase `paymentType` key only. The snake_case `payment_type`
   * that Midtrans sends is the customer's method (`qris`, `gopay`) and is
   * already carried, resolved, by `paymentMethod` — reading it here would
   * duplicate that value under a label meaning something else, and would do so
   * only on webhook-written rows, so it would look correct under test.
   */
  methodType: z.string().nullable(),
  /**
   * What the **gateway** says it took. `amount` is our own figure; the two
   * disagreeing is a discrepancy no column of ours can surface.
   *
   * Null rather than 0 when absent or unparseable — 0 asserts the gateway took
   * nothing, which is the most misleading value this field could carry.
   */
  grossAmount: z.number().nullable(),
  /**
   * The gateway's timestamp, against `paidAt` which is when **we** recorded it.
   * When they disagree, that gap is the diagnostic.
   *
   * Midtrans sends `"2026-07-30 14:23:11"` with no offset, always Asia/Jakarta.
   * Converted to ISO at +07:00 rather than passed through, so a consumer
   * calling `new Date()` on it cannot silently pick up the server's timezone
   * and mis-measure the very gap this field exists to show.
   */
  transactionTime: z.string().datetime().nullable(),
  /** Masked card, e.g. `"481111-1114"`. Never a full PAN. */
  maskedCardNumber: z.string().nullable(),
  cardBrand: z.string().nullable(),
  bankName: z.string().nullable(),
  vaNumber: z.string().nullable(),
  acquirer: z.string().nullable(),
});

export type AdminPaymentGatewayInfo = z.infer<typeof AdminPaymentGatewayInfoSchema>;

export const AdminPaymentOrderItemSchema = z.object({
  id: z.string(),
  ticketTypeName: z.string(),
  quantity: z.number().int(),
  unitPrice: z.number(),
  subtotal: z.number(),
});

export type AdminPaymentOrderItem = z.infer<typeof AdminPaymentOrderItemSchema>;

/**
 * The linked order, fetched from ticketing-service.
 *
 * payment-service's local `Order` projection holds no line items, so `items`
 * necessarily comes from a cross-service call behind a circuit breaker. The
 * whole object is null when that call fails — a degraded section beats a 500 on
 * the page an operator opens during an incident.
 */
export const AdminPaymentOrderInfoSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  items: z.array(AdminPaymentOrderItemSchema),
  total: z.number(),
});

export type AdminPaymentOrderInfo = z.infer<typeof AdminPaymentOrderInfoSchema>;

/**
 * A refund against this payment, from payment-service's own `Refund` table.
 *
 * `status` is {@link RefundStatusSchema} — the lowercase vocabulary
 * `refunds.service.ts` actually writes. Not to be confused with
 * {@link AdminRefundStatusSchema}, the uppercase `PENDING_APPROVAL`-style set
 * belonging to ticketing's refund flow on a different endpoint. Two layers, one
 * concept, different values, and they fail silently against each other — see
 * design doc §7.2, and the test pinning the admin badge config to this set.
 *
 * The admin app's old local `PaymentRefundItem` carried `reasonLabel` and
 * `notes`; neither has a column here. They were modelled on the other
 * endpoint's shape and are dropped rather than invented.
 */
export const AdminPaymentRefundSchema = z.object({
  id: z.string(),
  amount: z.number(),
  /** Free text on this table, not an enum. Nullable. */
  reason: z.string().nullable(),
  status: RefundStatusSchema,
  requestedBy: z.string(),
  approvedBy: z.string().nullable(),
  gatewayRefundId: z.string().nullable(),
  createdAt: z.string().datetime(),
  processedAt: z.string().datetime().nullable(),
});

export type AdminPaymentRefund = z.infer<typeof AdminPaymentRefundSchema>;

/**
 * One `PaymentLog` row.
 *
 * Modelled on what that table actually holds, not on what the UI mock imagined:
 * there is no actor column, so no `actor` field — `PaymentLog` records
 * `ipAddress` and `userAgent` but never who acted, and reporting a fabricated
 * "System" would state as fact something we did not record.
 *
 * This array is `[]` for every payment until #602 lands, because nothing in
 * `services/payment-service/src` writes `PaymentLog` — verified as zero rows
 * against live data. The field is wired so that when #602 starts writing, the
 * section fills with no further API change.
 */
export const AdminPaymentTimelineEventSchema = z.object({
  id: z.string(),
  /** e.g. `INITIATE`, `WEBHOOK`, `REFUND` — free text on the table. */
  action: z.string(),
  /** The payment status at the time, when the writer recorded one. */
  status: z.string().nullable(),
  timestamp: z.string().datetime(),
  /** `PaymentLog.errorMsg`, when the logged step failed. */
  note: z.string().nullable(),
});

export type AdminPaymentTimelineEvent = z.infer<typeof AdminPaymentTimelineEventSchema>;

/** Response of GET /api/v1/admin/payments/:id */
export const AdminPaymentDetailResponseSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  /** The payment's owner (#857). Nullable — see `PaymentResponseSchema`. */
  customerId: z.string().nullable(),
  /**
   * From the local `Order` projection, which is an optional Prisma relation.
   * Nullable, not optional: a payment whose order row was never projected has
   * no number, and the API says so rather than omitting the key and making the
   * frontend guess whether it was absent or unknown.
   */
  orderNumber: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
  paymentMethod: z.string(),
  status: PaymentStatusSchema,
  customerName: z.string(),
  customerEmail: z.string(),
  createdAt: z.string().datetime(),
  paidAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime(),
  gateway: AdminPaymentGatewayInfoSchema.nullable(),
  order: AdminPaymentOrderInfoSchema.nullable(),
  refunds: z.array(AdminPaymentRefundSchema),
  timeline: z.array(AdminPaymentTimelineEventSchema),
});

export type AdminPaymentDetailResponse = z.infer<typeof AdminPaymentDetailResponseSchema>;
